import { JSONType } from 'src/clientConfiguration';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import {
  BrowserIndexMap,
  BrowserType,
  OperatingSystemType,
} from 'src/kameleoonData';
import {
  CBSType,
  DeviceType,
  KCSType,
  OperatingSystemIndexMap,
} from 'src/kameleoonData/types';
import { Header } from './constants';
import { VisitorDataFiltersType } from 'src/utilities';
import { IUrlProvider } from './urlProvider';
import { LogLevel } from 'src/logging';

export enum UrlType {
  DataApi = 'dataApi',
  Events = 'events',
  ClientConfiguration = 'clientConfiguration',
}

export type DomainsType = {
  [key in UrlType]: string;
};

export type DomainType = {
  subdomain: string;
  topLevelDomain: string;
  secondLevelDomain: string;
};

export type UrlProviderInitializeParamsType = {
  domain: string | null;
  siteCode: string;
  packageInfo: ExternalPackageInfoType;
  environment: string;
};

export type RequesterParamsType = {
  useAbortController: boolean;
  urlProvider: IUrlProvider;
  packageInfo: ExternalPackageInfoType;
  externalRequester: IExternalRequester;
  trackRetryDelay: number;
  requestTimeout: number;
};

export type GetVisitorDataParamsType = {
  visitorCode: string;
  filters: VisitorDataFiltersType;
  isMappingIdentifier?: boolean;
};

export type GetVisitorDataUrlParamsType = {
  visitorCode: string;
  filters: VisitorDataFiltersType;
  isMappingIdentifier?: boolean;
};

type EventDataType = {
  sdk?: {
    name: string;
    version: string;
  };
  itp: boolean;
  time: number;
};

type CustomDataEventType = {
  data: {
    index: number;
    valuesCountMap: {
      [value: string]: number;
    };
    overwrite: boolean;
    mappingIdentifier: boolean;
  };
} & EventDataType;

type ExperimentEventType = {
  data: {
    id: number;
    variationId: number;
  };
} & EventDataType;

type ConversionEventType = {
  data: {
    goalId: number;
    revenue: number;
    negative: boolean;
    metadata?: CustomDataEventType[];
  };
} & EventDataType;

type BrowserIndexType = (typeof BrowserIndexMap)[BrowserType];
type OperatingSystemIndexType =
  (typeof OperatingSystemIndexMap)[OperatingSystemType];

export type PageEventType = {
  data: {
    href: string;
    canonicalHref: string | null;
    title: string;
    keyPagesIndices: number[] | [null];
    referrerHref: string | null;
    referrersIndices: number[] | [null];
  };
} & EventDataType;

export type GeolocationEventType = {
  data: {
    country: string;
    region: string | null;
    city: string | null;
    postalCode: string | null;
    latitude: number | null;
    longitude: number | null;
  };
} & EventDataType;

export type PersonalizationEventType = {
  data: {
    id: number;
    variationId: number;
  };
};

export type StaticDataEventType = {
  data: {
    visitNumber: number;
    timeSincePreviousVisit: number;
    firstReferrerHref: string | null;
    browser: BrowserType | null;
    browserIndex: BrowserIndexType | null;
    browserVersion: number | null;
    os: OperatingSystemType | null;
    osIndex: OperatingSystemIndexType | null;
    windowWidth: number | null;
    windowHeight: number | null;
    screenWidth: number | null;
    screenHeight: number | null;
    timeZoneId: string | null;
    localeLanguageTag: string | null;
    deviceType: DeviceType | null;
    mappingIdentifier: string | null;
  };
} & EventDataType;

export type VisitType = {
  siteCode: string;
  visitorCode: string;
  timeStarted: number;
  timeLastEvent?: number;
  customDataEvents?: CustomDataEventType[];
  experimentEvents?: ExperimentEventType[];
  conversionEvents?: ConversionEventType[];
  geolocationEvents?: GeolocationEventType[];
  staticDataEvent?: StaticDataEventType;
  pageEvents?: PageEventType[];
  personalizationEvents?: PersonalizationEventType[];
};

export type GetVisitorDataResultType = {
  previousVisits?: VisitType[];
  currentVisit?: VisitType;
  kcs?: KCSType;
  cbs?: CBSType;
};

export enum HttpMethod {
  Get = 'GET',
  Post = 'POST',
}

export enum DataApiQuery {
  VisitEvent,
  VisitData,
  DataMap,
}

const EVENT_TYPE_KEYS = [
  'customData',
  'staticData',
  'targetingSegment',
  'page',
  'conversion',
  'geolocation',
  'activity',
  'experiment',
] as const;
const PARAMETER_KEYS = [
  'valuesCountMap',
  'mappingValue',
  'id',
  'nonce',
  'country',
  'environment',
  'city',
  'region',
  'latitude',
  'geolocation',
  'longitude',
  'mappingIdentifier',
  'postalCode',
  'sdkName',
  'sdkVersion',
  'kcs',
  'cbs',
  'personalization',
  'browserIndex',
  'browserVersion',
  'siteCode',
  'visitorCode',
  'currentVisit',
  'maxNumberPreviousVisits',
  'customData',
  'negative',
  'metadata',
  'revenue',
  'title',
  'os',
  'conversion',
  'staticData',
  'osIndex',
  'browser',
  'referrersIndices',
  'overwrite',
  'index',
  'href',
  'experiment',
  'page',
  'variationId',
  'deviceType',
  'goalId',
  'ts',
  'key',
  'userAgent',
  'bodyUa',
  'timeSincePreviousVisit',
  'visitNumber',
] as const;
const TRACKING_KEYS = ['visit', 'map'] as const;
const QUERY_KEYS = ['sse', 'visitor', 'map', 'events'] as const;

export type TrackingType = Record<
  Capitalize<(typeof TRACKING_KEYS)[number]>,
  string
>;
export type QueryType = Record<Capitalize<(typeof QUERY_KEYS)[number]>, string>;
export type ParameterType = Record<
  Capitalize<(typeof PARAMETER_KEYS)[number]>,
  string
>;
export type EventType = Record<
  Capitalize<(typeof EVENT_TYPE_KEYS)[number]>,
  string
>;

export type StrictTrackingRecord<T> = {
  [K in keyof T]: `${Uncapitalize<K & string>}/`;
};
export type StrictQueryRecord<T> = {
  [K in keyof T]: `${Uncapitalize<K & string>}?siteCode=`;
};
export type StrictEventTypeRecord<T> = {
  [K in keyof T]: `eventType=${Uncapitalize<K & string>}`;
};
export type StrictParameterRecord<T> = {
  [K in keyof T]: `&${Uncapitalize<K & string>}=`;
};
export type StrictFirstParameterRecord<T> = {
  [K in keyof T]: `?${Uncapitalize<K & string>}=`;
};

/**
 * @type `HeadersType` - a type of headers available in the request
 * */
export type HeadersType = Partial<Record<Header, string>>;

/**
 * @type `RequestParametersType` - a type of parameters available in the request for each request type
 * */
export type RequestParametersType = {
  [RequestType.Configuration]: {
    headers?: HeadersType;
    signal: AbortSignal | null;
    method: HttpMethod.Get;
  };
  [RequestType.Tracking]: {
    headers?: HeadersType;
    body: string;
    signal: AbortSignal | null;
    method: HttpMethod.Post;
  };
  [RequestType.RemoteData]: {
    signal: AbortSignal | null;
    method: HttpMethod.Get;
  };
};

/**
 * @enum `RequestType` - an enum of available request types
 * */
export enum RequestType {
  Configuration = 'configuration',
  Tracking = 'tracking',
  RemoteData = 'remoteData',
}

/**
 * @type `RetryCountType` - a type of retry count available for each request type
 * @param {0 | 1 | 2} `RequestType.Configuration` - a count of retries for configuration request (`0` is the initial request)
 * @param {0 | 1 | 2}  `RequestType.Tracking` - a count of retries for tracking request (`0` is the initial request)
 * @param {0} `RequestType.RemoteData` - a count of retries for remote data request (`0` is the initial request)
 * */
export type RetryCountType = {
  [RequestType.Configuration]: 0 | 1 | 2;
  [RequestType.Tracking]: 0 | 1 | 2;
  [RequestType.RemoteData]: 0;
};

/**
 * @type `SendRequestParametersType` - a type of parameters available to send a request
 * @param {T extends RequestType} T - a type of request constrained by `RequestType`
 * @param {string} url - url address
 * @param {RequestParametersType[T]} parameters - parameters of the request depending on the request type
 * @param {RetryCountType | undefined} retryCount - a count of retries, the possible count of retries depends on `RequestType`
 * */
export type SendRequestParametersType<T extends RequestType> = {
  requestType: T;
  url: string;
  retryCount?: RetryCountType[T];
  parameters: RequestParametersType[T];
};

/**
 * @type `KameleoonResponse` - a type of response that is assignable to most libraries `Response` objects
 * @property {number} status - a status of the response
 * @property {() => Promise<JSONType>} json - a method to parse the response to JSON
 * @property {() => Promise<string>} text - a method to parse the response to text
 * @property {boolean} ok - a boolean value that indicates if the response is ok
 * @property {Headers} headers - a headers of the response
 * */
export type KameleoonResponseType = {
  status: number;
  json: () => Promise<JSONType>;
  text: () => Promise<string>;
  ok: boolean;
  headers?: Iterable<[string, string]>;
};

export type InternalSendRequestParametersType<T extends RequestType> = {
  url: string;
  requestType: RequestType;
  retryCount: number;
  parameters: Omit<RequestParametersType[T], 'signal'>;
};

/**
 * @interface `IExternalRequester` - an interface of External Requester which will be used to perform requests.
 * */
export interface IExternalRequester {
  /**
   * @method sendRequest - send a request
   * @param {SendRequestParametersType<T>} parameters - parameters of the request depending on the request type
   * @returns {Promise<KameleoonResponseType>} - a promise that resolves to the response
   * */
  sendRequest: <T extends RequestType>({
    requestType,
    url,
    parameters,
  }: SendRequestParametersType<T>) => Promise<KameleoonResponseType>;
}

export type logRequestParametersType = {
  logLevel: LogLevel;
  message: string;
  error?: unknown;
  method: HttpMethod;
  url: string;
  response?: KameleoonResponseType;
  headers?: HeadersType;
};

export type logResponse = {
  status: number;
  message: string;
};
